import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  RefreshCw,
  Play,
  CheckCircle,
  AlertCircle,
  Clock,
  Settings,
  Loader2,
  ArrowRight,
  FileText,
  RotateCcw,
  History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { vaultApi, notesApi } from '@/services/api';
import type { VaultSyncStatus, VaultSyncNoteItem, VaultSyncLog } from '@/types/notes.types';
import { format, parseISO } from 'date-fns';

export function VaultSyncSection() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingNoteId, setSyncingNoteId] = useState<number | null>(null);
  const [status, setStatus] = useState<VaultSyncStatus | null>(null);

  // Sync logs
  const [logs, setLogs] = useState<VaultSyncLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const isFetchingLogs = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vaultApi.getSyncStatus();
      setStatus(res.data.data);
    } catch {
      toast({ title: '获取同步状态失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchLogs = useCallback(async (pageNum = 1, append = false) => {
    if (isFetchingLogs.current) return;
    isFetchingLogs.current = true;
    if (!append) setLogsLoading(true);
    try {
      const response = await vaultApi.getSyncLogs({ page: pageNum, pageSize: 10 });
      const data = response.data;
      const results = data.results || [];
      const count = data.count || 0;

      if (append) {
        setLogs((prev) => [...prev, ...results]);
      } else {
        setLogs(results);
      }
      const currentLength = append ? logs.length + results.length : results.length;
      setHasMore(results.length === 10 && currentLength < count);
      setPage(pageNum);
    } catch {
      toast({ title: '获取同步记录失败', variant: 'destructive' });
    } finally {
      setLogsLoading(false);
      isFetchingLogs.current = false;
    }
  }, [toast, logs.length]);

  useEffect(() => {
    fetchStatus();
    fetchLogs(1, false);
  }, [fetchStatus, fetchLogs]);

  // 组件卸载时清理轮询定时器
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  /**
   * 触发全量同步后轮询日志与状态：
   * 每 3 秒一次，直到最新一条日志不再是「进行中」（最多 8 次）。
   * 后端日志由 Celery 子任务异步结算，running → success/failed/partial。
   */
  const pollSyncLogs = useCallback((attempt: number) => {
    pollTimerRef.current = setTimeout(async () => {
      try {
        const [statusRes, logsRes] = await Promise.all([
          vaultApi.getSyncStatus(),
          vaultApi.getSyncLogs({ page: 1, pageSize: 10 }),
        ]);
        setStatus(statusRes.data.data);
        const results = logsRes.data.results || [];
        const count = logsRes.data.count || 0;
        setLogs(results);
        setPage(1);
        setHasMore(results.length === 10 && results.length < count);
        if (results[0]?.status === 'running' && attempt < 8) {
          pollSyncLogs(attempt + 1);
        }
      } catch {
        // 轮询失败不打断用户，可手动点「刷新状态」
      }
    }, 3000);
  }, []);

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      const res = await vaultApi.sync();
      toast({
        title: '同步已启动',
        description: res.data.message || 'Vault 全量同步任务已触发',
      });
      // 启动轮询，直到日志结算完成
      pollSyncLogs(0);
    } catch (err: any) {
      const message = err?.response?.data?.message || '同步启动失败';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncNote = async (noteId: number) => {
    setSyncingNoteId(noteId);
    try {
      const res = await notesApi.syncToVault(noteId);
      toast({
        title: res.data.message || '同步已触发',
        description: `笔记 #${noteId}`,
      });
      // 2 秒后刷新状态
      setTimeout(() => fetchStatus(), 2000);
    } catch (err: any) {
      const message = err?.response?.data?.message || '单条同步失败';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setSyncingNoteId(null);
    }
  };

  const getStatusIcon = (s: string) => {
    switch (s) {
      case 'synced':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusLabel = (s: string) => {
    switch (s) {
      case 'synced':
        return '已同步';
      case 'failed':
        return '失败';
      default:
        return '待同步';
    }
  };

  const getLogStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getLogStatusText = (status: string) => {
    switch (status) {
      case 'running':
        return '进行中';
      case 'success':
        return '成功';
      case 'failed':
        return '失败';
      case 'partial':
        return '部分成功';
      default:
        return status;
    }
  };

  const getLogStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('zh-CN', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return value;
    }
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) return '-';
    try {
      return format(parseISO(value), 'yyyy-MM-dd HH:mm');
    } catch {
      return value;
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasIssues = (status?.failed ?? 0) > 0 || (status?.pending ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">总笔记</p>
                <p className="text-2xl font-bold">{status?.total ?? 0}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">已同步</p>
                <p className="text-2xl font-bold text-green-600">{status?.synced ?? 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">失败</p>
                <p className="text-2xl font-bold text-red-600">{status?.failed ?? 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">待同步</p>
                <p className="text-2xl font-bold text-yellow-600">{status?.pending ?? 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 上次同步时间 & 手动同步 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            手动同步
          </CardTitle>
          <CardDescription>
            {status?.lastSynced
              ? `上次成功同步: ${formatDate(status.lastSynced)}`
              : '尚未有成功同步记录'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Button onClick={handleFullSync} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? '同步中...' : '立即全量同步'}
          </Button>
          <Button variant="outline" onClick={fetchStatus} disabled={loading}>
            <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新状态
          </Button>
          <Link to="/settings/vault">
            <Button variant="ghost">
              <Settings className="w-4 h-4 mr-2" />
              配置 Vault
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* 失败笔记 */}
      {(status?.failedNotes?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              同步失败的笔记
            </CardTitle>
            <CardDescription>以下笔记上次同步到 Vault 时失败，可手动重试</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {status!.failedNotes.map((note) => (
                <NoteSyncRow
                  key={note.id}
                  note={note}
                  syncingNoteId={syncingNoteId}
                  onSync={handleSyncNote}
                  getStatusIcon={getStatusIcon}
                  getStatusLabel={getStatusLabel}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 待同步笔记 */}
      {(status?.pendingNotes?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600">
              <Clock className="w-5 h-5" />
              待同步的笔记
            </CardTitle>
            <CardDescription>以下笔记尚未同步到 Vault，保存时会自动触发同步</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {status!.pendingNotes.map((note) => (
                <NoteSyncRow
                  key={note.id}
                  note={note}
                  syncingNoteId={syncingNoteId}
                  onSync={handleSyncNote}
                  getStatusIcon={getStatusIcon}
                  getStatusLabel={getStatusLabel}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 全部正常 */}
      {!hasIssues && status && status.total > 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p className="text-lg font-medium text-gray-900">所有笔记同步正常</p>
            <p className="text-sm text-gray-500 mt-1">
              {status.synced} / {status.total} 条笔记已同步到 Vault
            </p>
          </CardContent>
        </Card>
      )}

      {/* 无笔记 */}
      {status && status.total === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium text-gray-900">暂无笔记</p>
            <p className="text-sm text-gray-500 mt-1">创建笔记后会自动同步到 Vault</p>
            <Link to="/notes/new">
              <Button className="mt-4 gap-2">
                <ArrowRight className="w-4 h-4" />
                创建笔记
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* 同步记录 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            同步记录
          </CardTitle>
          <CardDescription>历史 Vault 同步任务日志</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {logsLoading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">暂无同步记录</p>
            </div>
          ) : (
            <div className="divide-y">
              {logs.map((log) => {
                const logStatus = log.status ?? 'unknown';
                return (
                  <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        {getLogStatusIcon(logStatus)}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium">{log.syncTypeDisplay}同步</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLogStatusColor(logStatus)}`}>
                              {getLogStatusText(logStatus)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 space-y-0.5">
                            <p>
                              笔记 {log.notesTotal ?? 0} 条
                              {(log.notesSynced ?? 0) > 0 && ` · 成功 ${log.notesSynced}`}
                              {(log.notesFailed ?? 0) > 0 && ` · 失败 ${log.notesFailed}`}
                            </p>
                            {log.errorMessage && (
                              <p className="text-red-500">{log.errorMessage}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm text-gray-500 flex-shrink-0">
                        <p>{log.startedAt && formatDateTime(log.startedAt)}</p>
                        {log.durationMs != null && (
                          <p className="mt-1">耗时: {(log.durationMs / 1000).toFixed(1)}s</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="p-4 text-center border-t">
              <Button
                variant="outline"
                onClick={() => fetchLogs(page + 1, true)}
                disabled={logsLoading}
              >
                {logsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                加载更多
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// 单条笔记同步行
function NoteSyncRow({
  note,
  syncingNoteId,
  onSync,
  getStatusIcon,
  getStatusLabel,
  formatDate,
}: {
  note: VaultSyncNoteItem;
  syncingNoteId: number | null;
  onSync: (id: number) => void;
  getStatusIcon: (s: string) => React.ReactNode;
  getStatusLabel: (s: string) => string;
  formatDate: (v: string | null) => string;
}) {
  const isSyncing = syncingNoteId === note.id;

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {getStatusIcon(note.vaultSyncStatus)}
        <div className="min-w-0">
          <div className="font-medium truncate">{note.title}</div>
          <div className="text-sm text-gray-500">
            {getStatusLabel(note.vaultSyncStatus)} · 更新于 {formatDate(note.updatedAt)}
          </div>
          {note.error && (
            <div className="text-xs text-red-500 mt-0.5">{note.error}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link to={`/notes/${note.id}`}>
          <Button variant="ghost" size="sm">
            查看
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSync(note.id)}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <Loader2 className="w-4 h-4 animate-spin mr-1" />
          ) : (
            <Play className="w-4 h-4 mr-1" />
          )}
          同步
        </Button>
      </div>
    </div>
  );
}
